import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Divider,
  Select,
  TextInput,
} from '@/components/ui/daisy';
import { ValidateMaxGradeFn } from '@/data/hook/evaluation/useAssessmentGradeValidate';
import {
  AssessmentCriteria,
  AssessmentItem,
  AssessmentItemTypeEnum,
  TYPES_WITH_CRITERIA,
} from '@/models/Assessment';

import { adaptCriteriaAssessmentForm } from '@/utils/evaluation/adaptCriteriaAssessmentForm';
import AssessmentCriteriaForm from './AssessmentCriteriaForm';
import { SubmitArgs } from './AssessmentItemFormService';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import { ActivityBlockType } from '@/models/Activity';

export type AssessmentCriteriaField = {
  id?: number;
  name: string;
  description?: string;
  maxGrade?: number;
  activityType?: ActivityBlockType | '';
};

export type AssessmentItemFormFields = {
  name: string;
  description: string;
  type: AssessmentItemTypeEnum;
  maxGrade?: number;
  criteria: AssessmentCriteriaField[];
};

type AssessmentItemFormProps = {
  item?: AssessmentItem;
  onSubmit: (args: SubmitArgs) => void;
  onCancel: () => void;
  isLoading?: boolean;
  validateMaxGrade?: ValidateMaxGradeFn;
  maxGradeAvailable?: number;
};

type AssessmentItemFormBodyProps = Omit<AssessmentItemFormProps, 'item'> & {
  defaultValues: AssessmentItemFormFields;
};

export default function AssessmentItemForm({
  item,
  onSubmit,
  onCancel,
  isLoading,
  validateMaxGrade,
  maxGradeAvailable,
}: AssessmentItemFormProps) {
  const defaultValues = useMemo<AssessmentItemFormFields>(
    () => ({
      name: item?.name ?? '',
      description: item?.description ?? '',
      type: item?.type ?? AssessmentItemTypeEnum.MANUAL,
      maxGrade: item?.maxGrade ?? undefined,
      criteria: (item?.criteria ?? []).map(adaptCriteriaAssessmentForm),
    }),
    [item],
  );

  const methods = useForm<AssessmentItemFormFields>({ defaultValues });

  return (
    <FormProvider {...methods}>
      <AssessmentItemFormBody
        onSubmit={onSubmit}
        onCancel={onCancel}
        defaultValues={defaultValues}
        isLoading={isLoading}
        validateMaxGrade={validateMaxGrade}
        maxGradeAvailable={maxGradeAvailable}
      />
    </FormProvider>
  );
}

function AssessmentItemFormBody({
  onSubmit,
  onCancel,
  isLoading,
  defaultValues,
  validateMaxGrade,
  maxGradeAvailable,
}: AssessmentItemFormBodyProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useFormContext<AssessmentItemFormFields>();

  const [typeValue, criteriaValue] = watch(['type', 'criteria']);

  const applyCriterionSaved = useCallback(
    (index: number, saved: AssessmentCriteria) => {
      setValue(`criteria.${index}`, adaptCriteriaAssessmentForm(saved));
    },
    [setValue],
  );

  const resetCriteriaNames = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextType = event.target.value as AssessmentItemTypeEnum;
    if (!TYPES_WITH_CRITERIA.includes(nextType)) return;
    if (!TYPES_WITH_CRITERIA.includes(typeValue)) return;
    criteriaValue?.forEach((_, index) => {
      setValue(`criteria.${index}.name`, '', { shouldDirty: true });
      setValue(`criteria.${index}.activityType`, '', { shouldDirty: true });
    });
  };

  useEffect(() => {
    if (!isLoading) reset(defaultValues);
  }, [defaultValues, reset, isLoading]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    return handleSubmit(data =>
      onSubmit({ data, helpers: { applyCriterionSaved } }),
    )(event);
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleFormSubmit}>
      <TextInput.Fieldset
        label={t('assessmentItemName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('assessmentItemNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('assessmentItemNameRequired') })}
        />
      </TextInput.Fieldset>

      <TextInput.Fieldset label={t('assessmentItemDescription')}>
        <TextInput
          type="text"
          placeholder={t('assessmentItemDescriptionPlaceholder')}
          className="w-full"
          {...register('description')}
        />
      </TextInput.Fieldset>

      <Select.Fieldset
        label={t('assessmentItemType')}
        labelAlt={errors?.type?.message}
      >
        <Select
          className="w-full"
          color={!errors?.type?.message ? 'default' : 'error'}
          {...register('type', {
            required: t('assessmentItemTypeRequired'),
            validate: (value, formValues) =>
              TYPES_WITH_CRITERIA.includes(value) ||
              !formValues.criteria?.length ||
              t('assessmentItemTypeChangeBlockedByCriteria'),
            onChange: resetCriteriaNames,
          })}
        >
          <Select.Option value={AssessmentItemTypeEnum.MANUAL}>
            {t('assessmentItemTypeManual')}
          </Select.Option>
          <Select.Option value={AssessmentItemTypeEnum.DIGITAL_ACTIVITY}>
            {t('assessmentItemTypeDigitalActivity')}
          </Select.Option>
          <Select.Option value={AssessmentItemTypeEnum.TEST}>
            {t('assessmentItemTypeTest')}
          </Select.Option>
        </Select>
      </Select.Fieldset>

      <TextInput.Fieldset
        label={t('assessmentItemMaxGrade')}
        labelAlt={errors?.maxGrade?.message}
      >
        <TextInput.Wrapper
          color={!errors?.maxGrade?.message ? 'default' : 'error'}
          className="w-full"
        >
          <TextInput
            type="number"
            placeholder={t('assessmentItemMaxGradePlaceholder')}
            {...register('maxGrade', {
              required: t('assessmentItemMaxGradeRequired'),
              valueAsNumber: true,
              min: { value: 0, message: t('assessmentItemMaxGradeRange') },
              max: { value: 100, message: t('assessmentItemMaxGradeRange') },
              validate: validateMaxGrade,
            })}
          />
          <TextInput.Addon>
            <Badge size="xs" variant="soft">
              {t('assessmentItemMaxGradeMaxBadge', {
                max: maxGradeAvailable ?? 100,
              })}
            </Badge>
          </TextInput.Addon>
        </TextInput.Wrapper>
      </TextInput.Fieldset>

      <ConditionalRenderer
        condition={
          TYPES_WITH_CRITERIA.includes(typeValue) || criteriaValue.length > 0
        }
      >
        <Divider className="my-0" />
        <AssessmentCriteriaForm />
      </ConditionalRenderer>
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          className="grow bg-base-300"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          className="grow"
          disabled={isLoading || !isDirty}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
